import React from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import CKEditor from 'ckeditor4-react';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import { noop } from 'Helpers/PropTypes';
import { getBorderRadius } from 'Components/utils';

CKEditor.editorUrl = '/ckeditor/ckeditor.js';

const config = ({ disableTextColors, customConfig }) => ({
  toolbarGroups: [
    { name: 'document', groups: ['mode', 'document', 'doctools'] },
    { name: 'forms' },
    {
      name: 'basicstyles',
      groups: disableTextColors
        ? ['basicstyles', 'cleanup']
        : ['basicstyles', 'colors', 'cleanup'],
    },
    {
      name: 'paragraph',
      groups: ['list', 'indent', 'blocks', 'align', 'bidi'],
    },
    { name: 'links' },
    { name: 'styles' },
    { name: 'tools' },
    { name: 'insert' },
  ],
  pasteFromWordRemoveFontStyles: false,
  pasteFromWordRemoveStyles: false,
  allowedContent: true,
  // The default plugins included in the basic setup define some buttons that
  // are not needed in a basic editor. They are removed here.
  removeButtons: 'Cut,Copy,Paste,Undo,Redo,Anchor,Subscript,Superscript,Unlink,Source,Maximize,Table,Image,SpecialChar,HorizontalRule',
  // Dialog windows are also simplified.
  removeDialogTabs: 'link:advanced',
  removePlugins: 'elementspath,scayt,contextmenu,liststyle,tabletools,tableselection',
  width: '100%',
  height: '100%',
  disableNativeSpellChecker: false,
  toolbarCanCollapse: false,
  resize_enabled: false,
  contentsCss: '/ckeditor.css',
  colorButton_enableAutomatic: false,
  colorButton_historyRowLimit: 0,
  throttle: 150,
  ...customConfig,
  extraPlugins: `colorbutton,emoji${customConfig.extraPlugins ? `,${customConfig.extraPlugins}` : ''}`,
});

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    '& .cke': {
      width: '100%',
    },
    '& .cke_top': {
      display: 'flex',
      overflow: 'unset',
    },
    '& .cke_inner': {
      display: 'flex',
      flexDirection: 'column',
      borderRadius: theme.radius.xs,
      height: '100%',
      backgroundColor: 'initial',
    },
    '& .cke_contents': {
      backgroundColor: theme.palette.common.white,
      maxWidth: '100%',
      fontFamily: theme.typography.fontFamily,
      padding: theme.spacing(1),
      display: 'flex',
    },
    '& .cke_ltr': {
      border: 0,
    },
  },
  rootSizeDefault: {},
  rootSizeXs: {
    height: theme.dimensions.xs,
  },
  rootSizeSm: {
    height: theme.dimensions.sm,
  },
  rootSizeMd: {
    height: theme.dimensions.md,
  },
  rootSizeLg: {
    height: theme.dimensions.lg,
  },
  rootSizeXl: {
    height: theme.dimensions.xl,
  },
  rootSizeFull: {
    height: '100%',
  },
  rootHidden: {
    display: 'none',
  },
  editor: {
    display: 'flex',
  },
  disabled: {
    '& .cke': {
      '& > .cke_inner': {
        '& > .cke_contents': {
          backgroundColor: theme.palette.facet.mediumGray3,
          '& .cke_wysiwyg_frame': {
            backgroundColor: theme.palette.facet.mediumGray3,
          },
        },
      },
    },
  },
  borderless: {
    '& .cke': {
      border: 'none',
    },
    '& .cke_bottom': {
      backgroundColor: theme.palette.common.white,
      border: 'none',
    },
  },
  rounded: {
    ...getBorderRadius(theme.radius.md, { all: true }),
    '& .cke_bottom': {
      ...getBorderRadius(theme.radius.md, { bottom: true }),
    },
    '& .cke_contents': {
      ...getBorderRadius(theme.radius.md, { top: true }),
    },
  },
}));

const RichTextEditor = React.forwardRef(({
  className,
  onChange,
  size,
  value,
  disableTextColors,
  customConfig,
  test,
  type,
  onBeforeLoadProp,
  disabled,
  rounded,
  hidden,
  ...rest
}, ref) => {
  const classes = useStyles();
  const handleChange = (e) => {
    onChange(e.editor.getData());
  };

  const isBorderless = type === 'borderless';
  const isInline = type === 'inline';
  const paperVariant = !isInline && !isBorderless ? {
    variant: 'outlined',
  } : null;

  return (
    <Paper
      className={classNames(
        {
          [classes.root]: !isInline && !isBorderless,
          [classes.rootSizeDefault]: size === 'default',
          [classes.rootSizeXs]: size === 'xs',
          [classes.rootSizeSm]: size === 'sm',
          [classes.rootSizeMd]: size === 'md',
          [classes.rootSizeLg]: size === 'lg',
          [classes.rootSizeXl]: size === 'xl',
          [classes.rootSizeFull]: size === 'full',
          [classes.disabled]: disabled,
          [classes.rounded]: rounded,
          [classes.borderless]: isBorderless,
          [classes.rootHidden]: hidden,
        },
        className,
      )}
      {...paperVariant}
      test={test}
    >
      <CKEditor
        className={classes.editor}
        onChange={handleChange}
        data={value}
        config={config({
          disableTextColors,
          customConfig,
        })}
        type={type}
        ref={ref}
        onBeforeLoad={(editor) => {
          editor.on('instanceReady', () => {
            onBeforeLoadProp();
          });
        }}
        readOnly={disabled}
        {...rest}
      />
    </Paper>
  );
});

RichTextEditor.defaultProps = {
  className: '',
  value: null,
  size: 'default',
  disableTextColors: false,
  test: null,
  type: undefined,
  customConfig: {},
  onBeforeLoadProp: noop,
  disabled: false,
  rounded: false,
};

RichTextEditor.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'default']),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  disableTextColors: PropTypes.bool,
  test: PropTypes.string,
  type: PropTypes.string,
  customConfig: PropTypes.object,
  onBeforeLoadProp: PropTypes.func,
  disabled: PropTypes.bool,
  rounded: PropTypes.bool,
};

export default RichTextEditor;
